import { useFolderDragDrop } from "@/domains/documents/hooks/useFolderDragDrop";
import { useMoveDragOrigin } from "@/domains/documents/hooks/useMoveDragOrigin";
import { type MoveDragSelection } from "@/domains/documents/utils/documentMoveDrag";
import { useCallback, useEffect, useState } from "react";

import type { documentTableOptions } from "./documentTableOptions";
type Options = Pick<
  ReturnType<typeof documentTableOptions>,
  | "activeMoveProp"
  | "dragDropFolders"
  | "folders"
  | "onDropDocument"
  | "onMoveFolder"
  | "onDropSelection"
  | "onDropFiles"
  | "onInternalDragChange"
>;
export function useTableMoveState(options: Options) {
  const {
    activeMoveProp,
    dragDropFolders,
    folders,
    onDropDocument,
    onMoveFolder,
    onDropSelection,
    onDropFiles,
    onInternalDragChange,
  } = options;
  const [internalActiveMove, setInternalActiveMove] = useState<MoveDragSelection | null>(null);
  // The host owns the drag once it wires `activeMove`, so a drag started in the
  // folder tree reaches this table's targets. Standalone mounts (share page,
  // trash, child-documents dialog) keep the local state as the only source.
  const activeMove = activeMoveProp ?? internalActiveMove;
  const [dragSourceKey, setDragSourceKey] = useState<string | null>(null);
  const moveTargetFolders = dragDropFolders ?? folders;
  const pressedSelfHandledPart = useMoveDragOrigin();
  const folderDragDrop = useFolderDragDrop(
    moveTargetFolders,
    { onDropDocument, onMoveFolder, onDropSelection, onDropFiles },
    true,
    activeMove?.folderIds ?? []
  );

  const updateActiveMove = useCallback(
    (selection: MoveDragSelection | null, sourceKey: string | null = null) => {
      setInternalActiveMove(selection);
      setDragSourceKey(sourceKey);
      onInternalDragChange?.(selection);
    },
    [onInternalDragChange]
  );

  useEffect(
    () => () => {
      onInternalDragChange?.(null);
    },
    [onInternalDragChange]
  );
  return {
    pressedSelfHandledPart,
    moveTargetFolders,
    updateActiveMove,
    activeMove,
    dragSourceKey,
    folderDragDrop,
  };
}
